import { useMemo } from 'react'
import { DateTime } from 'luxon'

import { Alert, Card, CardColors, Popup, Text } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectLastYearExpenseRatio,
  selectLastYearExpenses,
  selectProjectedExpenses,
  selectYtdExpenseRatio,
  selectYtdExpenses,
} from '@/features/goal-setting/goals.selectors'
import { formatCurrencyWithDefault } from '@/features/goal-setting/helpers'
import { selectIsCatchUpBookkeepingComplete } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.selector'
import { TypographyStyles } from '@/styles'

export const ExpensesTab = () => {
  const catchUpBookkeepingComplete = useReselector(
    selectIsCatchUpBookkeepingComplete
  )
  const currentYear = useMemo(() => DateTime.now().year, [])
  const ytdExpenses = useReselector(selectYtdExpenses)
  const projectedExpenses = useReselector(selectProjectedExpenses)
  const lastYearExpenses = useReselector(selectLastYearExpenses)
  const expenseRatio = useReselector(selectYtdExpenseRatio)
  const lastYearExpenseRatio = useReselector(selectLastYearExpenseRatio)

  return (
    <>
      <Text variant={TypographyStyles.H1} className="mt-10">
        Expense Goal Help
      </Text>

      <Text variant={TypographyStyles.BODY_MD} className="mt-2">
        Expenses are the costs of running your business. Setting an expense
        target helps you manage spending. Comparing your expenses to your income
        (your “expense ratio”) is a helpful way to benchmark how much
        you&apos;re spending versus how much you&apos;re bringing in.
      </Text>

      <Alert
        title="Did you know?"
        description="Most therapy practices keep their expense ratios between 20-40%."
        className="mt-8"
      />

      <Text className="mt-8">
        Based upon your current performance, we’ve projected your expenses for
        the year to help you set a goal.
      </Text>

      <Card color={CardColors.LEVEL_1} className="mt-8">
        <Text variant={TypographyStyles.LABEL_MD}>This Year’s Expenses</Text>
        <div className="flex mt-6">
          <div className="w-1/2">
            <Text variant={TypographyStyles.LABEL_SM}>
              Projected {currentYear} Expenses
              <Popup
                content={
                  <>
                    <Text variant={TypographyStyles.H3}>
                      Projected Expenses
                    </Text>
                    <Text variant={TypographyStyles.BODY_SM} className="mt-3">
                      This is calculated by taking how much you&apos;ve spent
                      this year, and dividing it by how many days have already
                      passed. Then, to project out for the year, you multiply
                      this by the number of days in a year (365).
                    </Text>
                    <Text variant={TypographyStyles.BODY_SM}>
                      For example, as of May 10, you would be 130 days into the
                      year. If your expenses total $13,000 so far, divide
                      $13,000/130 days. This calculates to a rate of $100/day.
                    </Text>
                    <Text variant={TypographyStyles.BODY_SM}>
                      To project out for the rest of the year, multiply that
                      rate of $100/day by the number of days per year. This
                      would give you projected expenses of $36,500.
                    </Text>
                  </>
                }
              />
            </Text>
            <Text variant={TypographyStyles.DISPLAY_XS} className="mt-2">
              {formatCurrencyWithDefault(
                projectedExpenses.multiply(-1),
                catchUpBookkeepingComplete
              )}
            </Text>
            <Text variant={TypographyStyles.BODY_SM} className="mt-2">
              Based on YTD expenses of{' '}
              {formatCurrencyWithDefault(
                ytdExpenses * -1,
                catchUpBookkeepingComplete
              )}
            </Text>
          </div>
          <div className="w-1/2">
            <Text variant={TypographyStyles.LABEL_SM}>
              YTD Expense Ratio
              <Popup
                content={
                  <>
                    <Text variant={TypographyStyles.H3}>
                      Year to Date Expense Ratio
                    </Text>
                    <Text variant={TypographyStyles.BODY_SM}>
                      This is calculated by the amount you&apos;ve spent on
                      expenses this year divided by your year to date income, as
                      categorized by your Heard bookkeeping team.
                    </Text>
                  </>
                }
              />
            </Text>
            <Text variant={TypographyStyles.DISPLAY_XS} className="mt-2">
              {catchUpBookkeepingComplete ? expenseRatio : '-'}%
            </Text>
          </div>
        </div>
        {!catchUpBookkeepingComplete && (
          <Alert variant="warning" className="mt-6">
            We’ll be able to provide you with a projected expenses once your
            catch-up bookkeeping is complete.
          </Alert>
        )}
      </Card>

      {catchUpBookkeepingComplete && (
        <Card color={CardColors.LEVEL_1} className="mt-8">
          <Text variant={TypographyStyles.LABEL_MD}>Last Year’s Expenses</Text>
          <div className="flex mt-6">
            <div className="w-1/2">
              <Text variant={TypographyStyles.LABEL_SM}>
                {currentYear - 1} expenses
              </Text>
              <Text variant={TypographyStyles.DISPLAY_XS} className="mt-2">
                {formatCurrencyWithDefault(lastYearExpenses * -1)}
              </Text>
            </div>
            <div className="w-1/2">
              <Text variant={TypographyStyles.LABEL_SM}>
                {currentYear - 1} expenses ratio
              </Text>
              <Text variant={TypographyStyles.DISPLAY_XS} className="mt-2">
                {lastYearExpenseRatio}%
              </Text>
            </div>
          </div>
        </Card>
      )}
    </>
  )
}
